import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Accessibility, Play, Square, X } from "lucide-react";
import { useA11y } from "@/context/AccessibilityContext";
import { useLang } from "@/context/LanguageContext";

export default function AccessibilityWidget() {
  const { t, locale } = useLang();
  const { a11y, toggle } = useA11y();
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const panelRef = useRef(null);
  const speakingRef = useRef(false);
  const sectionsRef = useRef([]);
  const readIdxRef = useRef(0);
  const lastVisibleRef = useRef(-1);
  const scrollListenerRef = useRef(null);
  const jumpTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const stopReading = () => {
    window.speechSynthesis?.cancel();
    speakingRef.current = false;
    setSpeaking(false);
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
    if (jumpTimerRef.current) {
      clearTimeout(jumpTimerRef.current);
      jumpTimerRef.current = null;
    }
  };

  // se il componente si smonta, ferma la lettura
  useEffect(() => () => stopReading(), []);

  // un pulsante "ascolta" di una singola sezione interrompe la lettura globale
  useEffect(() => {
    const onSectionStart = () => {
      if (!speakingRef.current) return;
      if (scrollListenerRef.current) {
        window.removeEventListener("scroll", scrollListenerRef.current);
        scrollListenerRef.current = null;
      }
      if (jumpTimerRef.current) {
        clearTimeout(jumpTimerRef.current);
        jumpTimerRef.current = null;
      }
      speakingRef.current = false;
      setSpeaking(false);
    };
    window.addEventListener("gb-tts-section-start", onSectionStart);
    return () => window.removeEventListener("gb-tts-section-start", onSectionStart);
  }, []);

  const collectSections = () => {
    const scope = document.querySelectorAll("main section").length
      ? document.querySelectorAll("main section")
      : document.querySelectorAll("section");
    return Array.from(scope)
      .map((el) => ({ el, text: (el.innerText || "").replace(/\n{2,}/g, ". ").replace(/\n/g, ". ").trim() }))
      .filter((s) => s.text.length > 20);
  };

  // indice della sezione al centro dello schermo in questo momento
  const visibleSectionIndex = () => {
    const secs = sectionsRef.current;
    const mid = window.innerHeight / 2;
    for (let i = 0; i < secs.length; i++) {
      const r = secs[i].el.getBoundingClientRect();
      if (r.top <= mid && r.bottom >= mid) return i;
    }
    return 0;
  };

  const speakFrom = (i) => {
    const secs = sectionsRef.current;
    if (!speakingRef.current) return;
    if (i >= secs.length) {
      stopReading();
      return;
    }
    readIdxRef.current = i;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(secs[i].text);
    u.lang = locale;
    u.rate = 1;
    u.onend = () => {
      // finita la sezione, passa alla successiva — salvo salti dell'utente nel frattempo
      if (speakingRef.current && readIdxRef.current === i) speakFrom(i + 1);
    };
    window.speechSynthesis.speak(u);
  };

  const startReading = () => {
    if (!("speechSynthesis" in window)) return;
    sectionsRef.current = collectSections();
    if (!sectionsRef.current.length) return;
    window.dispatchEvent(new Event("gb-tts-global-start")); // spegne i pulsanti "ascolta" delle sezioni
    speakingRef.current = true;
    setSpeaking(true);
    // parte dalla sezione visibile, non dall'inizio della pagina
    const startIdx = visibleSectionIndex();
    lastVisibleRef.current = startIdx;
    // la voce segue la navigazione: cambiando sezione, legge quella nuova
    const onScroll = () => {
      if (!speakingRef.current) return;
      const v = visibleSectionIndex();
      if (v === lastVisibleRef.current) return;
      lastVisibleRef.current = v;
      if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
      jumpTimerRef.current = setTimeout(() => {
        if (speakingRef.current) speakFrom(lastVisibleRef.current);
      }, 600);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    scrollListenerRef.current = onScroll;
    speakFrom(startIdx);
  };

  const readPage = () => {
    if (speaking) {
      stopReading();
      return;
    }
    startReading();
  };

  return (
    <>
      <button
        type="button"
        data-testid="a11y-toggle-btn"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t("a11y.open")}
        className="fixed bottom-5 left-5 z-[90] flex h-12 w-12 items-center justify-center rounded-full border border-sky-400/50 bg-[#0C1220]/90 text-sky-300 shadow-[0_10px_30px_rgba(0,0,0,0.5)] backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-sky-300 hover:text-sky-200 focus:outline-none"
      >
        <Accessibility className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-label={t("a11y.title")}
            data-testid="a11y-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[4.75rem] left-5 z-[90] w-72 rounded-2xl border border-slate-700 bg-[#0C1220]/95 p-5 shadow-[0_24px_60px_rgba(0,0,0,0.6)] backdrop-blur-md"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg text-slate-50 tracking-wide">{t("a11y.title")}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("a11y.close")}
                data-testid="a11y-panel-close"
                className="rounded-full border border-slate-700 p-1.5 text-slate-400 hover:border-sky-400/60 hover:text-sky-300 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{t("a11y.desc")}</p>

            <button
              type="button"
              role="switch"
              aria-checked={a11y}
              data-testid="a11y-mode-switch"
              onClick={toggle}
              className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-slate-700/80 bg-slate-900/50 px-4 py-3 text-left transition-colors hover:border-sky-400/40 focus:outline-none"
            >
              <span>
                <span className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-100">{t("a11y.mode")}</span>
                <span className="mt-0.5 block text-[10px] text-slate-400 leading-snug">{t("a11y.modeHint")}</span>
              </span>
              <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${a11y ? "bg-sky-400" : "bg-slate-700"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${a11y ? "left-[1.375rem]" : "left-0.5"}`} />
              </span>
            </button>

            <button
              type="button"
              data-testid="a11y-listen-btn"
              onClick={readPage}
              className={`mt-2.5 flex w-full items-center justify-center gap-2.5 rounded-xl border px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] transition-colors duration-300 focus:outline-none ${
                speaking
                  ? "border-red-400/60 bg-red-400/10 text-red-300 hover:border-red-300"
                  : "border-sky-400/50 bg-sky-400/10 text-sky-300 hover:border-sky-300 hover:text-sky-200"
              }`}
            >
              {speaking ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {speaking ? t("a11y.stop") : t("a11y.listen")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
